<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
    @click="$emit('close')"
  >
    <div
      class="relative top-20 mx-auto p-5 border sm:max-w-2xl shadow-lg rounded-md bg-white"
      @click.stop
    >
      <div class="mt-2">
        <h3 class="text-lg font-medium text-gray-900 mb-3">
          {{ mode === 'create' ? 'Добавить акт' : 'Редактировать акт' }}
        </h3>
        <form @submit.prevent="submitForm" @click.stop class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-0.5">
                Договор
              </label>
              <div class="relative">
                <select
                  v-model="form.contract_id"
                  required
                  :disabled="disableContractField || contractsLoading"
                  :class="{
                    'mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500': true,
                    'bg-gray-100 cursor-not-allowed': disableContractField || contractsLoading
                  }"
                >
                  <option value="">
                    {{ contractsLoading ? 'Загрузка договоров...' : `Выберите договор (${contracts.length} доступно)` }}
                  </option>
                  <option
                    v-for="contract in contracts"
                    :key="contract.id"
                    :value="contract.id"
                  >
                    {{ contract.legal_name }}
                  </option>
                </select>
                <div
                  v-if="contractsLoading"
                  class="absolute right-10 top-1/2 transform -translate-y-1/2"
                >
                  <svg
                    class="animate-spin h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-0.5">
                Дата акта
              </label>
              <input
                type="date"
                v-model="form.date_of_act"
                required
                class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-0.5">
                Период платежа
              </label>
              <input
                type="month"
                v-model="form.payment_period"
                required
                class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-0.5">
                Номер акта
              </label>
              <input
                type="text"
                v-model="form.number_of_act"
                required
                class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-0.5">
                Сумма
              </label>
              <input
                type="number"
                v-model="form.amount"
                required
                min="0"
                step="0.01"
                class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-0.5">
              Комментарий
            </label>
            <textarea
              v-model="form.comment"
              rows="2"
              class="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            ></textarea>
          </div>

          <div>
            <div class="flex items-center">
              <input
                v-model="form.draft"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900"> Черновик </label>
            </div>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="$emit('close')"
              class="inline-flex justify-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Отмена
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="inline-flex justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span v-if="loading">Сохранение...</span>
              <span v-else>{{
                mode === 'create' ? 'Создать акт' : 'Сохранить'
              }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, onMounted } from 'vue'
  import { billingApi } from '../services/api'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit'].includes(value)
    },
    act: {
      type: Object,
      default: () => ({})
    },
    currentContractId: {
      type: [String, Number],
      default: ''
    },
    disableContractField: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['close', 'submit'])

  const loading = ref(false)
  const contractsLoading = ref(false)
  const contracts = ref([])

  const form = ref({
    contract_id: '',
    date_of_act: '',
    payment_period: '',
    number_of_act: '',
    amount: '',
    comment: '',
    draft: false
  })

  const loadContracts = async () => {
    contractsLoading.value = true
    try {
      const response = await billingApi.get('/contract', {
        params: { limit: 999999 }
      })
      contracts.value = response.data.contracts

      if (!form.value.contract_id && props.currentContractId) {
        form.value.contract_id = props.currentContractId
      }
    } catch (err) {
      console.error('Error loading contracts:', err)
    } finally {
      contractsLoading.value = false
    }
  }

  const resetForm = () => {
    form.value = {
      contract_id: props.currentContractId || '',
      date_of_act: '',
      payment_period: '',
      number_of_act: '',
      amount: '',
      comment: '',
      draft: false
    }
  }

  const closeModal = () => {
    emit('close')
    // Очищаем форму при закрытии
    resetForm()
  }

  const submitForm = async () => {
    loading.value = true
    try {
      const formData = {
        contract_id: parseInt(form.value.contract_id),
        date_of_act: form.value.date_of_act,
        payment_period: form.value.payment_period + '-01',
        number_of_act: form.value.number_of_act,
        amount: parseInt(form.value.amount),
        comment: form.value.comment,
        draft: form.value.draft
      }

      emit('submit', formData)
    } catch (err) {
      console.error('Error in form submission:', err)
    } finally {
      loading.value = false
    }
  }

  watch(
    () => props.act,
    newAct => {
      if (newAct && Object.keys(newAct).length > 0) {
        form.value = {
          id: newAct.id,
          contract_id: props.disableContractField
            ? props.currentContractId
            : newAct.contract_id || props.currentContractId,
          date_of_act: newAct.date_of_act,
          payment_period: newAct.payment_period
            ? newAct.payment_period.substring(0, 7)
            : '',
          number_of_act: newAct.number_of_act,
          amount: newAct.amount,
          comment: newAct.comment || '',
          draft: newAct.draft || false
        }
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    loadContracts()
  })

  watch(
    () => props.show,
    newShow => {
      if (newShow) {
        loadContracts()
      }
    }
  )
</script>
